import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Grid,
  CircularProgress,
} from '@mui/material';
import { formatCurrency } from '../../../utils/formatters';
import NumeroInternoMeta from '../../../components/NumeroInternoMeta';
import pedidoVentaService, { PedidoVenta } from '../../../services/pedidoVentaService';

interface PedidoDetalleProps {
  open: boolean;
  onClose: () => void;
  pedidoId: number | null;
}

const PedidoDetalle: React.FC<PedidoDetalleProps> = ({ open, onClose, pedidoId }) => {
  const [pedido, setPedido] = useState<PedidoVenta | null>(null);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && pedidoId) {
      loadPedido();
    } else {
      setPedido(null);
    }
  }, [open, pedidoId]);

  const loadPedido = async () => {
    if (!pedidoId) return;
    try {
      setLoading(true);
      const data = await pedidoVentaService.getPedidoVenta(pedidoId);
      // Convertir fechas si vienen como strings (sin mutar el objeto original)
      const pedidoData = {
        ...data,
        fechaEmision: data.fechaEmision
          ? (typeof data.fechaEmision === 'string' ? new Date(data.fechaEmision) : data.fechaEmision)
          : new Date(),
        horaEmision: data.horaEmision
          ? (typeof data.horaEmision === 'string' ? new Date(data.horaEmision) : data.horaEmision)
          : new Date(),
      };
      setPedido(pedidoData);
    } catch (error: any) {
      console.error('Error al cargar pedido:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoLabel = (asentado: string) => {
    return asentado === 'S' ? 'Asentado' : 'Pendiente';
  };

  const getEstadoColor = (asentado: string): 'success' | 'warning' => {
    return asentado === 'S' ? 'success' : 'warning';
  };

  const totalArticulos = pedido?.detalles?.length || 0;
  const totalUnidades = pedido?.detalles?.reduce((sum, d) => sum + (d.cantidad || 0), 0) || 0;
  // Calcular el total sumando los subtotales de todos los items
  // Si el subtotal no está presente, calcularlo como precioUnitario * cantidad
  const totalCalculado = pedido?.detalles?.reduce((sum, d) => {
    const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
    return sum + subtotal;
  }, 0) || 0;
  // Usar el total calculado si importeTotal es 0 o no está presente
  const total = pedido?.importeTotal && pedido.importeTotal > 0 ? pedido.importeTotal : totalCalculado;

  const formatHora = (fecha: Date | string) => {
    const d = fecha instanceof Date ? fecha : new Date(fecha);
    return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatFecha = (fecha: Date | string) => {
    const d = fecha instanceof Date ? fecha : new Date(fecha);
    return d.toLocaleDateString('es-AR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Detalle del Pedido</Typography>
          {pedido && (
            <Chip
              label={getEstadoLabel(pedido.asentado)}
              color={getEstadoColor(pedido.asentado)}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : pedido ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Información General */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Información General
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Número de Pedido:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.nroFormulario}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Fecha de Emisión:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatFecha(pedido.fechaEmision)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Hora:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatHora(pedido.horaEmision)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Descripción:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.descripcion || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Usuario:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.usuario || 'Sistema'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Cliente */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Cliente
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Nombre:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.nombre || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Número de Cliente:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.nroCliente || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tipo:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.tipoCliente || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Información Comercial */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Información Comercial
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Vendedor:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.nroVendedor || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Lista de Precios:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.nroLista || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tipo de Formulario:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.nroTipoFormulario || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Moneda:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pedido.codigoMoneda === 'ARS' ? 'Pesos' : pedido.codigoMoneda || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Total */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Total
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'success.main',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                {formatCurrency(total)}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  {totalArticulos} {totalArticulos === 1 ? 'Artículo' : 'Artículos'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  {totalUnidades} {totalUnidades === 1 ? 'Unidad' : 'Unidades'}
                </Typography>
              </Box>
            </Box>

            {/* Detalle de Artículos */}
            {pedido.detalles && pedido.detalles.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Detalle de Artículos ({totalArticulos})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {pedido.detalles.map((detalle, index) => (
                    <Box
                      key={`${detalle.nroItem}-${index}`}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {/* Header con Item # y cantidad */}
                      <Box 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center" 
                        mb={2.5}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main',
                            fontSize: '1rem'
                          }}
                        >
                          Item #{detalle.nroItem || index + 1}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 700, 
                            color: 'primary.main',
                            fontSize: '1rem'
                          }}
                        >
                          x{detalle.cantidad || 0}
                        </Typography>
                      </Box>

                      {/* Información del artículo */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Código:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                            {detalle.codigoArticulo || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Descripción:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {detalle.descripcionArticulo || '-'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Precio Unitario:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                            {formatCurrency(detalle.precioUnitario || 0)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Subtotal */}
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: '1px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Subtotal:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: 700, 
                              color: 'success.main',
                              fontSize: '1rem'
                            }}
                          >
                            {formatCurrency(
                              detalle.subtotal || 
                              ((detalle.precioUnitario || 0) * (detalle.cantidad || 0))
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Notas */}
                      {detalle.notas && detalle.notas.trim() && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Notas:
                          </Typography>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: 'warning.light',
                              border: '1px solid',
                              borderColor: 'warning.main',
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.primary',
                                fontWeight: 500,
                                fontSize: '0.9rem'
                              }}
                            >
                              {detalle.notas}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No se pudo cargar el detalle del pedido.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Cerrar
        </Button>
        <NumeroInternoMeta nro={pedido?.nroFormulario} />
      </DialogActions>
    </Dialog>
  );
};

export default PedidoDetalle;

